import { Link } from 'react-router-dom';
import SectionHeading from '../components/SectionHeading';
import CTAButton      from '../components/CTAButton';
import Reveal         from '../components/Reveal';
import { SERVICES_DETAILED, SERVICES_SPECIALISED, STEPS, TECHSTACK } from '../constant/data';

const STATS = [
  { value: '20+',  label: 'Systems Deployed'  },
  { value: '200+', label: 'Businesses Hosted'  },
  { value: '4',    label: 'Countries Served'   },
  { value: '7+',   label: 'Years in Business'  },
];

function Services() {
  return (
    <>
      {/* ── Page Header ─────────────────────────────────────── */}
      <section className="page-header">
        <div className="container">
          <div className="breadcrumb-nav">
            <Link to="/">Home</Link>
            <span>/</span>
            Services
          </div>
          <h1>What We Do</h1>
          <p>
            End-to-end technology services — from the domain name to the deployed enterprise
            system. Most clients start with one thing and come back for more.
          </p>
        </div>
      </section>

      {/* ── Stats Bar ───────────────────────────────────────── */}
      <section className="features-bar">
        <div className="container">
          <div className="row g-3 justify-content-center">
            {STATS.map(s => (
              <div className="col-6 col-md-3" key={s.label}>
                <div className="feature-item">
                  <span className="feature-num">{s.value}</span>
                  <span className="feature-label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Core Services ───────────────────────────────────── */}
      <section className="section-corporate">
        <div className="container">
          <SectionHeading
            label="Our Services"
            align="center"
            subtitle="Everything you need to get online, build something powerful, and keep it running."
          >
            Six Ways We Can Help
          </SectionHeading>

          <div className="row g-4">
            {SERVICES_DETAILED.map((s, idx) => (
              <Reveal key={s.title} delay={idx * 60} className="col-lg-4 col-md-6">
                <div className="service-card service-card--featured" style={{ height: '100%' }}>
                  <div
                    className="service-icon"
                    style={{ width: 52, height: 52, fontSize: '1.2rem', marginBottom: '1.5rem' }}
                  >
                    <i className={s.icon} />
                  </div>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: '0.85rem' }}>{s.title}</h4>
                  <p style={{ fontSize: '0.88rem', lineHeight: 1.75, flex: 1 }}>{s.description}</p>
                  <ul className="capability-list" style={{ marginBottom: '1.5rem' }}>
                    {s.capabilities.map((cap, i) => (
                      <li key={i}>
                        <i className="fas fa-circle" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                  <CTAButton type="outline-navy" to="/contact">Get in Touch</CTAButton>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Specialised Capabilities ────────────────────────── */}
      <section
        className="section-corporate"
        style={{
          background: 'var(--surface-2)',
          borderTop: '1px solid var(--border-1)',
          borderBottom: '1px solid var(--border-1)',
        }}
      >
        <div className="container">
          <SectionHeading
            label="Specialised Capabilities"
            align="center"
            subtitle="Deep-expertise services for organisations with complex operational requirements."
          >
            Also in Our Toolkit
          </SectionHeading>

          <div className="row g-3">
            {SERVICES_SPECIALISED.map((s, idx) => (
              <Reveal key={s.title} delay={idx * 50} className="col-lg-4 col-md-6">
                <div
                  className="card-corporate"
                  style={{ height: '100%', padding: '1.75rem' }}
                >
                  <div className="card-icon">
                    <i className={s.icon} />
                  </div>
                  <h5 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>{s.title}</h5>
                  <p style={{ fontSize: '0.83rem', marginBottom: '1rem', lineHeight: 1.65 }}>
                    {s.description}
                  </p>
                  <ul
                    className="capability-list"
                    style={{ borderTop: '1px solid var(--border-1)', paddingTop: '0.85rem', margin: 0 }}
                  >
                    {s.capabilities.map((cap, i) => (
                      <li key={i}>
                        <i className="fas fa-circle" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How We Work ─────────────────────────────────────── */}
      <section className="section-corporate">
        <div className="container">
          <SectionHeading
            align="center"
            label="Our Process"
            subtitle="We follow a simple process — understand the problem, design the right solution, build it properly, and stay involved after launch."
          >
            How We Work
          </SectionHeading>

          <div className="row g-4">
            {STEPS.map((s, idx) => (
              <Reveal key={s.title} delay={idx * 70} className="col-md-3 col-6">
                <div className="value-card" style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      background: 'var(--ink-1)',
                      borderRadius: '50%',
                      color: 'var(--surface-1)',
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      lineHeight: '36px',
                      textAlign: 'center',
                      margin: '0 auto 1rem',
                    }}
                  >
                    {s.num}
                  </div>
                  <div className="value-icon" style={{ marginBottom: '0.75rem' }}>
                    <i className={s.icon} />
                  </div>
                  <h5 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>{s.title}</h5>
                  <p style={{ fontSize: '0.82rem' }}>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech Stack ──────────────────────────────────────── */}
      <section
        className="techstack-section"
        style={{ borderTop: '1px solid var(--border-1)', borderBottom: '1px solid var(--border-1)' }}
      >
        <div className="container">
          <p
            className="partners-label"
            style={{ marginBottom: '1.5rem' }}
          >
            Technologies we build with
          </p>
          <div className="techstack-grid">
            {TECHSTACK.map(t => (
              <div key={t.name} className="tech-chip">
                <i className={t.icon} />
                {t.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="cta-section">
        <div className="container text-center position-relative">
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '0.35rem 0.9rem',
              borderRadius: 50,
              border: '1px solid rgba(255,255,255,0.15)',
              marginBottom: '1.25rem',
            }}
          >
            Let's Talk
          </span>
          <h2>Not sure where to start?</h2>
          <p>
            Tell us what you're dealing with and we'll figure out together what makes sense.
            No pitch, no pressure — just an honest conversation.
          </p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <CTAButton type="gold" to="/contact">Get in Touch</CTAButton>
            <CTAButton type="outline-light" href="mailto:info@salamasystems.com">
              Email Us Directly
            </CTAButton>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
